// ─────────────────────────────────────────────────────────────
// The SFE version this install runs and the state of its DGI attestation
// (#622 D), from GET /api/license/sfe-version (any signed-in user). The API
// writes the notice; the screen shows it as is.
// ─────────────────────────────────────────────────────────────
import { useQuery } from '@tanstack/react-query';
import { useBillingApi } from '../../billing-host/api';

/** NOT_ATTESTED: the version holds no DGI attestation of conformity. */
export type SfeVersionState = 'NOT_ATTESTED' | 'ATTESTED' | 'SUSPENDED' | 'WITHDRAWN' | 'RE_ESTABLISHED';

/** The DGI decision in force on this install's version. */
export interface SfeVersionDecision {
  attestationNumber: string;
  state: 'SUSPENDED' | 'WITHDRAWN' | 'RE_ESTABLISHED';
  notifiedAt: string;
  effectiveFrom: string;
  responseDeadline: string | null;
  reference: string;
  summary: string | null;
}

export interface SfeVersionStatus {
  version: string;
  fingerprint: string | null;
  source: 'REGISTRY' | 'LIST';
  state: SfeVersionState;
  decision: SfeVersionDecision | null;
  /** Certified signing is refused on this install. */
  frozen: boolean;
  /** French notice shown on every screen; null unless SUSPENDED or WITHDRAWN. */
  notice: string | null;
  /** The « pin to version X » instruction that applies to this install (#631); null when none does. */
  pin?: SfeVersionPin | null;
  /**
   * True only when the API says the running build is the attested one;
   * absent from an API that does not send it, read as not certified.
   */
  homologated?: boolean;
}

/** The publisher's request to return to an older homologated version, shown, never applied. */
export interface SfeVersionPin {
  pinTo: string;
  tag: string;
  reference: string;
  effectiveFrom: string;
  /** French notice shown on every screen. */
  notice: string;
}

export const sfeVersionKeys = {
  status: () => ['sfe-version'] as const,
  build: () => ['sfe-version', 'build'] as const,
};

/** The suite version and the SFE build this server runs (#259), for signed-in users only. */
export interface SfeBuild {
  /** Null when the server cannot read its suite version. */
  suiteVersion: string | null;
  version: string;
  /** Null when the server has no build info. */
  fingerprint: string | null;
  /** « Suite … — SFE x.y.z (empreinte …) », worded by the server; the SFE part alone without a suite version. */
  label: string;
}

/** GET /api/license/sfe-build — the running build, never shown on the public status page. */
export function useSfeBuild() {
  const api = useBillingApi();
  return useQuery({
    queryKey: sfeVersionKeys.build(),
    queryFn: () => api.get<SfeBuild>('/license/sfe-build'),
    staleTime: 15 * 60_000,
    retry: false,
  });
}

/** GET /api/license/sfe-version — drives the version banner of every screen. */
export function useSfeVersion() {
  const api = useBillingApi();
  return useQuery({
    queryKey: sfeVersionKeys.status(),
    queryFn: () => api.get<SfeVersionStatus>('/license/sfe-version'),
    staleTime: 5 * 60_000,
    refetchInterval: 15 * 60_000,
    retry: false,
  });
}
